import React, { useRef, useState } from "react";
import { API_BASE_URL } from "src/config/apiBase";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Sparkles,
  UploadCloud,
  X,
  Link as LinkIcon,
  Image as ImageIcon,
  RefreshCw,
  Check,
  AlertTriangle,
  Loader2,
  Wand2,
  Plus,
  Trash2,
  Search,
  HelpCircle,
  Tag,
  Copy,
} from "lucide-react";
import { decryptToken } from "../utils/getToken";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import "./AIProductAssistant.css";

/**
 * AI Product Assistant — isolated AI layer placed above the Product Title on the
 * Simple / Variable / Group product forms. It generates editable marketing
 * content only and NEVER touches SKU, price, MRP, stock, GST, weight, shipping,
 * discounts or variations.
 *
 * Props:
 *  - productType: "simple" | "variable" | "group"
 *  - onApply(patch): receives a partial formData patch ready to merge:
 *      { title, short_description, description,
 *        key_features: [{id,text}], specifications: [{id,label,value}] }
 */

const GEN_STEPS = [
  "Analyzing product images…",
  "Reading packaging & labels…",
  "Generating title & descriptions…",
  "Building specifications…",
  "Finalizing content…",
];

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** Trim and ensure https:// on pasted product links. */
const normalizeUrl = (raw) => {
  let u = String(raw || "").trim().replace(/^[\s"'<>]+|[\s"'<>]+$/g, "");
  if (!u) return "";
  if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
  return u;
};

const AIProductAssistant = ({ productType = "simple", onApply, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [mode, setMode] = useState("image"); // image | url
  const [images, setImages] = useState([]); // { file, preview }
  const [url, setUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [useAsImages, setUseAsImages] = useState(true); // upload AI images to product
  const [applying, setApplying] = useState(false);

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");

  const [result, setResult] = useState(null); // editable generated content
  const [analysis, setAnalysis] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [regenField, setRegenField] = useState("");

  // V2 extras (display-only — no matching form fields yet)
  const [seo, setSeo] = useState(null);
  const [faqs, setFaqs] = useState(null);
  const [v2Loading, setV2Loading] = useState("");

  const fileInputRef = useRef(null);
  const stepTimer = useRef(null);

  const token = decryptToken("accessToken") || "";
  const user = getDecryptedUserFromCookie("gta_mainstateuser") || {};

  /**
   * Uploads the AI-assistant images to the existing media library
   * (POST /api/images/upload) so they can be used as the product's image and
   * gallery. Returns an array of { url } objects matching the form's shape.
   */
  const uploadImagesToLibrary = async () => {
    const uploaded = [];
    for (const img of images) {
      try {
        const fd = new FormData();
        fd.append("image", img.file);
        fd.append("user_id", user.main_id ?? "");
        fd.append("user_name", user.full_name ?? "");
        const res = await axios.post(`${API_BASE_URL}/api/images/upload`, fd, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          timeout: 60000,
        });
        if (res?.data?.url) uploaded.push({ url: res.data.url });
      } catch (err) {
        // Surface but don't abort applying the generated text content.
        toast.error(
          err.response?.data?.error || "Failed to upload one of the images."
        );
      }
    }
    return uploaded;
  };

  /* --------------------------------------------------------------- images */

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) =>
      f.type?.startsWith("image/")
    );
    if (!incoming.length) return;
    setImages((prev) => {
      const merged = [...prev];
      for (const f of incoming) {
        if (merged.length >= 5) break;
        merged.push({ file: f, preview: URL.createObjectURL(f) });
      }
      if (prev.length + incoming.length > 5) {
        toast.info("You can upload up to 5 images.");
      }
      return merged;
    });
  };

  const removeImage = (idx) => {
    setImages((prev) => {
      const copy = [...prev];
      const [removed] = copy.splice(idx, 1);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return copy;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  /* ------------------------------------------------------------- progress */

  const startStepAnimation = () => {
    setStep(0);
    let s = 0;
    stepTimer.current = setInterval(() => {
      s += 1;
      if (s < GEN_STEPS.length) setStep(s);
      else clearInterval(stepTimer.current);
    }, 1500);
  };

  const stopStepAnimation = () => {
    if (stepTimer.current) clearInterval(stepTimer.current);
    setStep(0);
  };

  /* ------------------------------------------------------------- generate */

  const callGenerate = async ({ fields } = {}) => {
    setError("");

    const normalizedUrl = normalizeUrl(url);
    const hasImages = images.length > 0;
    const hasUrl = mode === "url" && normalizedUrl;

    if (!fields) {
      if (mode === "image" && !hasImages) {
        setError("Please upload at least one product image.");
        return;
      }
      if (mode === "url" && !hasUrl) {
        setError("Please paste a product URL.");
        return;
      }
    }

    let payload;
    let useJson = false;

    if (fields && analysis) {
      payload = {
        product_type: productType,
        analysis,
        fields,
      };
      useJson = true;
    } else if (mode === "url") {
      payload = {
        product_type: productType,
        url: normalizedUrl,
      };
      useJson = true;
    } else {
      const fd = new FormData();
      fd.append("product_type", productType);
      images.forEach((img) => fd.append("images", img.file));
      payload = fd;
    }

    setLoading(true);
    if (!fields) startStepAnimation();
    setRegenField(fields ? fields[0] : "");

    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/ai/product/generate`,
        payload,
        {
          headers: useJson
            ? {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              }
            : {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
              },
          timeout: 120000,
        }
      );

      if (!res.data?.success) {
        throw new Error(res.data?.message || "Generation failed.");
      }

      const data = res.data.data;
      if (data.analysis) setAnalysis(data.analysis);
      setConfidence(typeof data.confidence === "number" ? data.confidence : null);

      setResult((prev) => {
        const base =
          fields && prev
            ? { ...prev }
            : {
                title: "",
                short_description: "",
                description: "",
                key_features: [],
                specifications: [],
                suggested_attributes: [],
                tags: [],
              };

        const apply = (key) => {
          if (key === "key_features") {
            base.key_features = (data.key_features || []).map((t) => ({
              id: newId(),
              text: t,
            }));
          } else if (key === "specifications") {
            base.specifications = (data.specifications || []).map((s) => ({
              id: newId(),
              label: s.label,
              value: s.value,
            }));
          } else if (key === "suggested_attributes") {
            base.suggested_attributes = data.suggested_attributes || [];
          } else if (key === "tags") {
            base.tags = data.tags || [];
          } else {
            base[key] = data[key] ?? base[key] ?? "";
          }
        };

        const keys = fields || [
          "title",
          "short_description",
          "description",
          "key_features",
          "specifications",
          "suggested_attributes",
          "tags",
        ];
        keys.forEach(apply);
        return base;
      });

      if (fields) toast.success(`Regenerated ${fields[0].replace(/_/g, " ")}`);
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Generation failed.";
      setError(msg);
    } finally {
      setLoading(false);
      stopStepAnimation();
      setRegenField("");
    }
  };

  /* ----------------------------------------------------- editable updates */

  const updateField = (key, value) =>
    setResult((prev) => ({ ...prev, [key]: value }));

  const updateFeature = (id, text) =>
    setResult((prev) => ({
      ...prev,
      key_features: prev.key_features.map((f) =>
        f.id === id ? { ...f, text } : f
      ),
    }));

  const removeFeature = (id) =>
    setResult((prev) => ({
      ...prev,
      key_features: prev.key_features.filter((f) => f.id !== id),
    }));

  const addFeature = () =>
    setResult((prev) => ({
      ...prev,
      key_features: [...prev.key_features, { id: newId(), text: "" }],
    }));

  const updateSpec = (id, patch) =>
    setResult((prev) => ({
      ...prev,
      specifications: prev.specifications.map((s) =>
        s.id === id ? { ...s, ...patch } : s
      ),
    }));

  const removeSpec = (id) =>
    setResult((prev) => ({
      ...prev,
      specifications: prev.specifications.filter((s) => s.id !== id),
    }));

  const addSpec = () =>
    setResult((prev) => ({
      ...prev,
      specifications: [
        ...prev.specifications,
        { id: newId(), label: "", value: "" },
      ],
    }));

  /* ---------------------------------------------------------------- apply */

  const applyToForm = async () => {
    if (!result) return;
    const patch = {
      title: result.title || "",
      short_description: result.short_description || "",
      description: result.description || "",
      key_features: (result.key_features || [])
        .filter((f) => f.text && f.text.trim())
        .map((f) => ({ id: f.id || newId(), text: f.text.trim() })),
      specifications: (result.specifications || [])
        .filter((s) => s.label?.trim() && s.value?.trim())
        .map((s) => ({
          id: s.id || newId(),
          label: s.label.trim(),
          value: s.value.trim(),
        })),
    };
    if (result.tags?.length) patch.tags = result.tags;

    // Also upload the AI images as the product's image + gallery (opt-in).
    if (mode === "image" && images.length && useAsImages) {
      setApplying(true);
      const uploaded = await uploadImagesToLibrary();
      setApplying(false);
      if (uploaded.length) {
        patch.image = [uploaded[0]];
        patch.gallery = uploaded.slice(1);
      }
    }

    onApply?.(patch);
    toast.success("AI content applied to the form. Review before saving.");
  };

  const applySeo = () => {
    if (!seo) return;
    onApply?.({
      meta_title: seo.meta_title || "",
      meta_description: seo.meta_description || "",
      meta_keywords: seo.focus_keywords || [],
    });
    toast.success("SEO applied to the form.");
  };

  const applyFaqs = () => {
    if (!faqs?.faqs?.length) return;
    onApply?.({ faqs: faqs.faqs });
    toast.success("FAQs applied to the form.");
  };

  /* ------------------------------------------------------------- V2 calls */

  const copyText = (text) => {
    if (!text) return;
    navigator.clipboard
      ?.writeText(text)
      .then(() => toast.success("Copied"))
      .catch(() => {});
  };

  const callV2 = async (kind) => {
    if (!analysis) {
      toast.info("Generate product content first.");
      return;
    }
    setV2Loading(kind);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/ai/product/${kind}`,
        {
          product_type: productType,
          analysis,
          content: result
            ? {
                title: result.title,
                short_description: result.short_description,
                description: result.description,
              }
            : undefined,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          timeout: 90000,
        }
      );

      if (!res.data?.success) {
        throw new Error(res.data?.message || "Generation failed.");
      }

      if (kind === "seo") setSeo(res.data.data);
      else if (kind === "faqs") setFaqs(res.data.data);
      else if (kind === "tags")
        setResult((prev) => ({ ...prev, tags: res.data.data.tags || [] }));

      toast.success(`${kind.toUpperCase()} generated`);
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Generation failed.";
      toast.error(msg);
    } finally {
      setV2Loading("");
    }
  };

  const reset = () => {
    images.forEach((i) => i.preview && URL.revokeObjectURL(i.preview));
    setImages([]);
    setUrl("");
    setResult(null);
    setAnalysis(null);
    setConfidence(null);
    setSeo(null);
    setFaqs(null);
    setError("");
  };

  /* ----------------------------------------------------------------- view */

  const lowConfidence = confidence != null && confidence < 60;

  return (
    <div className="aipa">
      <div className="aipa__head" onClick={() => setOpen((o) => !o)}>
        <div className="aipa__title">
          <span className="aipa__badge">
            <Sparkles size={18} />
          </span>
          <div className="aipa__title-text">
            <h3>
              {open ? (
                <>
                  AI Product Assistant <span className="aipa__pill">AI</span>
                </>
              ) : (
                "Generate Product Content Using AI"
              )}
            </h3>
            {open && (
              <p>Generate title, descriptions, features &amp; specifications</p>
            )}
          </div>
        </div>
        <button type="button" className="aipa__toggle">
          {open ? "Hide" : "Show"}
        </button>
      </div>

      {open && (
        <div className="aipa__body">
          {/* Source tabs */}
          <div className="aipa__tabs">
            <button
              type="button"
              className={`aipa__tab ${mode === "image" ? "is-active" : ""}`}
              onClick={() => setMode("image")}
            >
              <ImageIcon size={15} /> From Images
            </button>
            <button
              type="button"
              className={`aipa__tab ${mode === "url" ? "is-active" : ""}`}
              onClick={() => setMode("url")}
            >
              <LinkIcon size={15} /> From Product URL
            </button>
          </div>

          {/* Image mode */}
          {mode === "image" && (
            <>
              <div
                className={`aipa__drop ${isDragging ? "is-drag" : ""}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <UploadCloud size={30} />
                <p className="aipa__drop-title">Drag &amp; drop images here</p>
                <p className="aipa__drop-sub">
                  or click to browse — up to 5 images (front, back, packaging)
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />

              {images.length > 0 && (
                <>
                  <div className="aipa__thumbs">
                    {images.map((img, idx) => (
                      <div key={idx} className="aipa__thumb">
                        <img src={img.preview} alt={`upload-${idx}`} />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          aria-label="Remove image"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <label className="aipa__checkbox">
                    <input
                      type="checkbox"
                      checked={useAsImages}
                      onChange={(e) => setUseAsImages(e.target.checked)}
                    />
                    Use these images as the product image &amp; gallery
                  </label>
                </>
              )}
            </>
          )}

          {/* URL mode */}
          {mode === "url" && (
            <div className="aipa__url">
              <input
                type="url"
                placeholder="https://www.amazon.in/... or Flipkart / supplier product page"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onBlur={(e) => setUrl(normalizeUrl(e.target.value))}
              />
              <p className="aipa__hint">
                Paste the full product detail page link (not search results).
                Manufacturer and supplier pages work best. Amazon/Flipkart may
                block some links — use images if URL fails.
              </p>
            </div>
          )}

          {/* Generate */}
          <div className="aipa__actions">
            <button
              type="button"
              className="aipa__generate"
              disabled={loading}
              onClick={() => callGenerate()}
            >
              {loading && !regenField ? (
                <>
                  <Loader2 size={16} className="aipa__spin" /> Generating…
                </>
              ) : (
                <>
                  <Wand2 size={16} /> Generate Product Content
                </>
              )}
            </button>
            {(result || images.length > 0 || url) && (
              <button
                type="button"
                className="aipa__reset"
                disabled={loading}
                onClick={reset}
              >
                Reset
              </button>
            )}
          </div>

          {/* Progress */}
          {loading && !regenField && (
            <div className="aipa__progress">
              <Loader2 size={15} className="aipa__spin" />
              <span>{GEN_STEPS[step]}</span>
            </div>
          )}

          {error && (
            <div className="aipa__error">
              <AlertTriangle size={15} /> {error}
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="aipa__result">
              <div className="aipa__result-head">
                <div className="aipa__checklist">
                  {result.title && (
                    <span>
                      <Check size={13} /> Title
                    </span>
                  )}
                  {result.short_description && (
                    <span>
                      <Check size={13} /> Short Description
                    </span>
                  )}
                  {result.description && (
                    <span>
                      <Check size={13} /> Description
                    </span>
                  )}
                  {result.specifications?.length > 0 && (
                    <span>
                      <Check size={13} /> Specifications
                    </span>
                  )}
                  {result.key_features?.length > 0 && (
                    <span>
                      <Check size={13} /> Features
                    </span>
                  )}
                </div>

                {confidence != null && (
                  <div
                    className={`aipa__confidence ${
                      lowConfidence ? "is-low" : ""
                    }`}
                  >
                    <div className="aipa__confidence-top">
                      <span>AI Confidence</span>
                      <strong>{confidence}%</strong>
                    </div>
                    <div className="aipa__confidence-track">
                      <div
                        className="aipa__confidence-fill"
                        style={{ width: `${confidence}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {lowConfidence && (
                <div className="aipa__warn">
                  <AlertTriangle size={14} /> Low confidence — please verify the
                  specifications manually.
                </div>
              )}

              {/* Title */}
              <div className="aipa__field">
                <div className="aipa__field-head">
                  <label>Title</label>
                  <button
                    type="button"
                    className="aipa__regen"
                    disabled={loading}
                    onClick={() => callGenerate({ fields: ["title"] })}
                  >
                    <RefreshCw size={12} /> Regenerate
                  </button>
                </div>
                <input
                  type="text"
                  value={result.title}
                  onChange={(e) => updateField("title", e.target.value)}
                />
              </div>

              {/* Short description */}
              <div className="aipa__field">
                <div className="aipa__field-head">
                  <label>Short Description</label>
                  <button
                    type="button"
                    className="aipa__regen"
                    disabled={loading}
                    onClick={() =>
                      callGenerate({ fields: ["short_description"] })
                    }
                  >
                    <RefreshCw size={12} /> Regenerate
                  </button>
                </div>
                <textarea
                  rows={2}
                  value={result.short_description}
                  onChange={(e) =>
                    updateField("short_description", e.target.value)
                  }
                />
              </div>

              {/* Description */}
              <div className="aipa__field">
                <div className="aipa__field-head">
                  <label>Description (HTML)</label>
                  <button
                    type="button"
                    className="aipa__regen"
                    disabled={loading}
                    onClick={() => callGenerate({ fields: ["description"] })}
                  >
                    <RefreshCw size={12} /> Regenerate
                  </button>
                </div>
                <textarea
                  rows={5}
                  value={result.description}
                  onChange={(e) => updateField("description", e.target.value)}
                />
              </div>

              {/* Key features */}
              <div className="aipa__field">
                <div className="aipa__field-head">
                  <label>Key Features</label>
                  <div className="aipa__field-head-actions">
                    <button
                      type="button"
                      className="aipa__regen"
                      disabled={loading}
                      onClick={() => callGenerate({ fields: ["key_features"] })}
                    >
                      <RefreshCw size={12} /> Regenerate
                    </button>
                    <button
                      type="button"
                      className="aipa__mini"
                      onClick={addFeature}
                    >
                      <Plus size={12} /> Add
                    </button>
                  </div>
                </div>
                {result.key_features.map((f) => (
                  <div key={f.id} className="aipa__row">
                    <input
                      type="text"
                      value={f.text}
                      placeholder="Feature"
                      onChange={(e) => updateFeature(f.id, e.target.value)}
                    />
                    <button
                      type="button"
                      className="aipa__del"
                      onClick={() => removeFeature(f.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Specifications */}
              <div className="aipa__field">
                <div className="aipa__field-head">
                  <label>Specifications (Label / Value)</label>
                  <div className="aipa__field-head-actions">
                    <button
                      type="button"
                      className="aipa__regen"
                      disabled={loading}
                      onClick={() =>
                        callGenerate({ fields: ["specifications"] })
                      }
                    >
                      <RefreshCw size={12} /> Regenerate
                    </button>
                    <button
                      type="button"
                      className="aipa__mini"
                      onClick={addSpec}
                    >
                      <Plus size={12} /> Add
                    </button>
                  </div>
                </div>
                {result.specifications.map((s) => (
                  <div key={s.id} className="aipa__row aipa__row--spec">
                    <input
                      type="text"
                      value={s.label}
                      placeholder="Label"
                      onChange={(e) =>
                        updateSpec(s.id, { label: e.target.value })
                      }
                    />
                    <input
                      type="text"
                      value={s.value}
                      placeholder="Value"
                      onChange={(e) =>
                        updateSpec(s.id, { value: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      className="aipa__del"
                      onClick={() => removeSpec(s.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Suggested attributes (variable products) — display only */}
              {productType === "variable" &&
                result.suggested_attributes?.length > 0 && (
                  <div className="aipa__field">
                    <label>Detected Attributes</label>
                    <div className="aipa__attrs">
                      {result.suggested_attributes.map((a, i) => (
                        <span key={i} className="aipa__attr">
                          <strong>{a.attribute}:</strong>{" "}
                          {(a.values || []).join(", ")}
                        </span>
                      ))}
                    </div>
                    <p className="aipa__hint">
                      Use these as a guide. Variations are never auto-created —
                      add sizes/colors manually in the variation generator.
                    </p>
                  </div>
                )}

              {/* Tags — preview only; use footer Apply to Form */}
              {result.tags?.length > 0 && (
                <div className="aipa__field">
                  <label>Suggested Tags</label>
                  <div className="aipa__attrs">
                    {result.tags.map((t, i) => (
                      <span key={i} className="aipa__tag">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer — all action buttons grouped */}
              <div className="aipa__footer">
                <span className="aipa__footer-label">More options</span>
                <div className="aipa__footer-tools">
                  <button
                    type="button"
                    className="aipa__v2-btn"
                    disabled={!!v2Loading}
                    onClick={() => callV2("seo")}
                  >
                    {v2Loading === "seo" ? (
                      <Loader2 size={14} className="aipa__spin" />
                    ) : (
                      <Search size={14} />
                    )}
                    Generate SEO
                  </button>
                  <button
                    type="button"
                    className="aipa__v2-btn"
                    disabled={!!v2Loading}
                    onClick={() => callV2("faqs")}
                  >
                    {v2Loading === "faqs" ? (
                      <Loader2 size={14} className="aipa__spin" />
                    ) : (
                      <HelpCircle size={14} />
                    )}
                    Generate FAQs
                  </button>
                  <button
                    type="button"
                    className="aipa__v2-btn"
                    disabled={!!v2Loading}
                    onClick={() => callV2("tags")}
                  >
                    {v2Loading === "tags" ? (
                      <Loader2 size={14} className="aipa__spin" />
                    ) : (
                      <Tag size={14} />
                    )}
                    Regenerate Tags
                  </button>
                </div>

                {seo && (
                  <div className="aipa__v2-card">
                    <div className="aipa__v2-row">
                      <span className="aipa__v2-label">Meta Title</span>
                      <button
                        type="button"
                        className="aipa__copy"
                        onClick={() => copyText(seo.meta_title)}
                      >
                        <Copy size={12} /> Copy
                      </button>
                    </div>
                    <p className="aipa__v2-val">{seo.meta_title || "—"}</p>

                    <div className="aipa__v2-row">
                      <span className="aipa__v2-label">Meta Description</span>
                      <button
                        type="button"
                        className="aipa__copy"
                        onClick={() => copyText(seo.meta_description)}
                      >
                        <Copy size={12} /> Copy
                      </button>
                    </div>
                    <p className="aipa__v2-val">{seo.meta_description || "—"}</p>

                    {seo.focus_keywords?.length > 0 && (
                      <>
                        <span className="aipa__v2-label">Focus Keywords</span>
                        <div className="aipa__attrs">
                          {seo.focus_keywords.map((k, i) => (
                            <span key={i} className="aipa__tag">
                              {k}
                            </span>
                          ))}
                        </div>
                      </>
                    )}

                    <div className="aipa__v2-apply">
                      <button
                        type="button"
                        className="aipa__apply"
                        onClick={applySeo}
                      >
                        <Check size={15} /> Apply SEO
                      </button>
                    </div>
                  </div>
                )}

                {faqs?.faqs?.length > 0 && (
                  <div className="aipa__v2-card">
                    <span className="aipa__v2-label">FAQs</span>
                    {faqs.faqs.map((f, i) => (
                      <div key={i} className="aipa__faq">
                        <p className="aipa__faq-q">Q: {f.question}</p>
                        <p className="aipa__faq-a">A: {f.answer}</p>
                      </div>
                    ))}
                    <div className="aipa__v2-apply">
                      <button
                        type="button"
                        className="aipa__apply"
                        onClick={applyFaqs}
                      >
                        <Check size={15} /> Apply FAQs
                      </button>
                    </div>
                  </div>
                )}

                <p className="aipa__disclaimer">
                  AI never sets SKU, price, stock, GST, shipping or variations.
                  All content is editable before you save.
                </p>

                <div className="aipa__footer-actions">
                  <button
                    type="button"
                    className="aipa__regen-all"
                    disabled={loading}
                    onClick={() => callGenerate()}
                  >
                    <RefreshCw size={14} /> Regenerate Everything
                  </button>
                  <button
                    type="button"
                    className="aipa__apply"
                    disabled={applying}
                    onClick={applyToForm}
                  >
                    {applying ? (
                      <>
                        <Loader2 size={16} className="aipa__spin" /> Uploading
                        images…
                      </>
                    ) : (
                      <>
                        <Check size={16} /> Apply to Form
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIProductAssistant;
